import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft, ArrowRight, Mail, CheckCircle2, Smartphone, Shield,
  Globe, Users, Cloud, RefreshCw, Lock,
} from "lucide-react";
import { motion } from "framer-motion";
import mobileDeviceHeroBg from "@/assets/mobile-device-hero-bg.jpg";

import outlookIcon from "@/assets/ms-icons/outlook.svg";
import onedriveIcon from "@/assets/ms-icons/onedrive.svg";
import teamsIcon from "@/assets/ms-icons/teams.svg";
import sharepointIcon from "@/assets/ms-icons/sharepoint.svg";
import wordIcon from "@/assets/ms-icons/word.svg";
import excelIcon from "@/assets/ms-icons/excel.svg";
import powerpointIcon from "@/assets/ms-icons/powerpoint.svg";
import onenoteIcon from "@/assets/ms-icons/onenote.svg";
import intuneIcon from "@/assets/ms-icons/intune.svg";
import defenderIcon from "@/assets/ms-icons/defender.svg";

type FeatureItem = {
  icon: string;
  text: string;
};

const features: FeatureItem[] = [
  { icon: outlookIcon, text: "Zakelijke e-mail met Exchange Online (2 GB mailbox)" },
  { icon: wordIcon, text: "Webversies van Word, Excel, PowerPoint en OneNote" },
  { icon: teamsIcon, text: "Microsoft Teams voor chatten, bellen en videobellen" },
  { icon: onedriveIcon, text: "2 GB OneDrive-opslag" },
  { icon: sharepointIcon, text: "SharePoint Online voor documenttoegang" },
  { icon: intuneIcon, text: "Beveiligingsbeleid via Microsoft Intune" },
  { icon: defenderIcon, text: "Multifactor-authenticatie" },
  { icon: defenderIcon, text: "Doorlopende offsite back-ups" },
  { icon: intuneIcon, text: "Volledig beheer door Rinqel" },
];

const highlights = [
  {
    icon: Smartphone,
    title: "Gemaakt voor mobiel",
    text: "Speciaal voor medewerkers die onderweg werken op een tablet of telefoon. Geen vaste computer nodig.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: Mail,
    title: "Zakelijke e-mail",
    text: "Professioneel mailen met je eigen domeinnaam. Alles gesynchroniseerd op je mobiele apparaat.",
    color: "bg-hero-gradient",
  },
  {
    icon: Users,
    title: "Teams & samenwerken",
    text: "Blijf verbonden met je collega's via Teams. Chatten, bellen en videobellen — ook onderweg.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Shield,
    title: "Beveiligd apparaat",
    text: "Via Intune wordt je apparaat beveiligd en beheerd. Wordt het gestolen? Dan wissen we het op afstand.",
    color: "bg-rinqel-green-gradient",
  },
];

const WerkplekMobileDevice = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden">
        <img src={mobileDeviceHeroBg} alt="Mobiel apparaatbeheer: zakelijke telefoons en tablets beveiligd" loading="eager" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="container relative z-10 flex min-h-[420px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                Op basis van Microsoft 365 F3
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <Smartphone className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Managed Mobile{" "}
              <span className="text-white/90">Device Only</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Voor medewerkers die alleen een tablet of telefoon gebruiken. Zakelijke e-mail, Teams en beveiliging — zonder vaste computer.
            </motion.p>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="text-4xl font-bold text-white">€17,50</span>
              <span className="ml-1 text-white/70">/per maand per gebruiker</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wat zit erin */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Compact maar <span className="text-gradient">compleet</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Niet iedereen heeft een volledige werkplek nodig. Voor medewerkers met alleen een iPad,
              tablet of telefoon is de Mobile Device Only-werkplek de perfecte oplossing.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 max-w-2xl">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
              <ul className="space-y-3">
                {features.map((f, i) => (
                  <motion.li
                    key={f.text}
                    className="flex items-center gap-3 text-sm"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                     transition={{ duration: 0.3, delay: i * 0.04 }}
                   >
                     <img loading="lazy" decoding="async" src={f.icon} alt={f.text.split("(")[0].trim()} className="h-5 w-5 shrink-0 object-contain" />
                    <span>{f.text}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ideaal voor{" "}
            <span className="text-gradient">onderweg</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Voorbeelden */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Denk bijvoorbeeld aan
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Monteurs met een iPad, verkopers op pad, magazijnmedewerkers met een tablet of chauffeurs
              die onderweg hun mail en planning moeten bekijken. Ze hebben geen laptop nodig — alleen
              een veilige, beheerde mobiele werkplek.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Vraag aan" trackLocation="werkplek-mobile-device-cta">
                Vraag aan
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default WerkplekMobileDevice;
